import { isCapacitorPlatform } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { differenceInSeconds } from "date-fns";
import { HyperTrack, HyperTrackError } from "hypertrack-sdk-ionic-capacitor";
import { useCallback, useEffect, useRef, useState } from "react";

const SUPPRESS_ERRORS_THRESHOLD_SECONDS = 30;
const TIMEOUT_THRESHOLD_SECONDS = 60;

type VerificationState = "loading" | "connected" | "failed";

interface UseHyperTrackVerificationStatusProps {
  shiftId: string;
  workerId: string;
}

interface UseHyperTrackVerificationStatusResult {
  state: VerificationState;
  errorReason: string | undefined;
  retry: () => void;
}

const STATIC_ERROR_REASON_MAP: Partial<Record<HyperTrackError, string>> = {
  [HyperTrackError.permissionsLocationDenied]: "because location permissions are not granted",
  [HyperTrackError.permissionsLocationNotDetermined]:
    "because location permissions are not granted",
  [HyperTrackError.permissionsLocationRestricted]: "because location permissions are not granted",
  [HyperTrackError.locationServicesDisabled]: "because location services seem to be turned off",
  [HyperTrackError.locationSignalLost]: "because we lost GPS signal",
  [HyperTrackError.locationMocked]: "because a mock location was detected",
};

const ERROR_REASON_MAP: Partial<Record<HyperTrackError, string>> = {
  ...STATIC_ERROR_REASON_MAP,
  [HyperTrackError.permissionsLocationReducedAccuracy]: "because precise location is not enabled",
};

function getErrorReasonFromErrors(errors: HyperTrackError[]): string | undefined {
  for (const error of errors) {
    const reason = ERROR_REASON_MAP[error];
    if (reason) {
      return reason;
    }
  }

  return undefined;
}

/**
 * Subscribes to HyperTrack location and error events to determine location state.
 * Errors are suppressed for the first 30s to allow the SDK to settle. After 30s, SDK
 * errors are surfaced with a mapped reason. After 60s, the hook times out regardless
 * of SDK state.
 */
export function useHyperTrackVerificationStatus(
  props: UseHyperTrackVerificationStatusProps
): UseHyperTrackVerificationStatusResult {
  const { shiftId, workerId } = props;

  const [verificationState, setVerificationState] = useState<VerificationState>("loading");
  const [errorReason, setErrorReason] = useState<string | undefined>(undefined);

  const startTimeRef = useRef(Date.now());
  const timeoutTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const suppressionTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const latestErrorsRef = useRef<HyperTrackError[]>([]);
  const hasResolvedRef = useRef(false);
  const hasConnectedRef = useRef(false);

  const logTransition = useCallback(
    (newState: VerificationState, params?: { reason?: string; errorType?: string }) => {
      if (newState === "connected") {
        logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_LOCATION_VERIFICATION_CONNECTED, {
          shiftId,
          workerId,
        });
      } else if (newState === "failed") {
        logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_LOCATION_VERIFICATION_FAILED, {
          shiftId,
          workerId,
          errorReason: params?.reason,
          errorType: params?.errorType,
        });
      }
    },
    [shiftId, workerId]
  );

  const transitionToFailed = useCallback(
    (reason: string | undefined, errorType: string) => {
      if (hasResolvedRef.current) {
        return;
      }

      hasResolvedRef.current = true;
      setErrorReason(reason);
      setVerificationState("failed");
      logTransition("failed", { reason, errorType });
    },
    [logTransition]
  );

  const transitionToConnected = useCallback(() => {
    // Avoid re-logging the analytics event on repeated location callbacks
    if (hasConnectedRef.current) {
      return;
    }

    hasConnectedRef.current = true;
    hasResolvedRef.current = true;
    setVerificationState("connected");
    setErrorReason(undefined);
    logTransition("connected");
  }, [logTransition]);

  const startTimers = useCallback(() => {
    clearTimeout(suppressionTimerRef.current);
    clearTimeout(timeoutTimerRef.current);

    // After 30s, if still loading, check if there are pending errors to show
    suppressionTimerRef.current = setTimeout(() => {
      if (hasResolvedRef.current) {
        return;
      }

      if (latestErrorsRef.current.length > 0) {
        transitionToFailed(getErrorReasonFromErrors(latestErrorsRef.current), "sdk_error");
      }
    }, SUPPRESS_ERRORS_THRESHOLD_SECONDS * 1000);

    // After 60s, timeout regardless of state
    timeoutTimerRef.current = setTimeout(() => {
      if (hasResolvedRef.current) {
        return;
      }

      hasResolvedRef.current = true;
      setErrorReason(undefined);
      setVerificationState("failed");
      logTransition("failed", { errorType: "timeout" });
    }, TIMEOUT_THRESHOLD_SECONDS * 1000);
  }, [transitionToFailed, logTransition]);

  const initialize = useCallback(() => {
    hasResolvedRef.current = false;
    hasConnectedRef.current = false;
    startTimeRef.current = Date.now();
    latestErrorsRef.current = [];
    setVerificationState("loading");
    setErrorReason(undefined);
    startTimers();
  }, [startTimers]);

  useEffect(() => {
    initialize();

    return () => {
      clearTimeout(suppressionTimerRef.current);
      clearTimeout(timeoutTimerRef.current);
    };
  }, [initialize]);

  useEffect(() => {
    if (!isCapacitorPlatform()) {
      return;
    }

    const locationUnsubscribe = HyperTrack.subscribeToLocation((location) => {
      if (location.type === "success") {
        transitionToConnected();
      }
    });

    const errorsUnsubscribe = HyperTrack.subscribeToErrors((errors) => {
      latestErrorsRef.current = errors;

      if (errors.length === 0) {
        return;
      }

      const elapsedSeconds = differenceInSeconds(Date.now(), startTimeRef.current);
      if (elapsedSeconds >= SUPPRESS_ERRORS_THRESHOLD_SECONDS) {
        transitionToFailed(getErrorReasonFromErrors(errors), "sdk_error");
      }
    });

    return () => {
      void locationUnsubscribe.remove();
      void errorsUnsubscribe.remove();
    };
  }, [shiftId, workerId, transitionToConnected, transitionToFailed]);

  return { state: verificationState, errorReason, retry: initialize };
}
